import { ROICalculator } from '#components/ROICalculator';
import {
  Badge,
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';
import { FaBolt, FaChartLine, FaFileInvoiceDollar } from 'react-icons/fa/index.js';

const MotionBox = motion(Box);

const CASE_STATS = [
  { stat: '$45K', label: 'Outsourcing costs eliminated for an AEC firm', color: 'green' },
  { stat: '27%', label: 'Total IT/telecom cost reduction for a nonprofit', color: 'blue' },
  { stat: '$25,670', label: 'Saved for a medical practice over 17 months', color: 'purple' },
];

const CalculatorPage = () => {
  const pageBg = useColorModeValue('gray.50', 'gray.950');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box bg={pageBg}>
      {/* Hero */}
      <Box
        bgGradient="linear(to-br, gray.900, blue.900)"
        py={{ base: 20, md: 28 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute" top="-80px" right="-80px"
          w="400px" h="400px" borderRadius="full"
          bg="whiteAlpha.50" pointerEvents="none"
        />
        <Container maxW="container.lg" textAlign="center">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 } as any}
          >
            <Badge bg="blue.500" color="white" fontSize="sm" px={4} py={1} borderRadius="full" mb={6} display="inline-block">
              Free Calculators
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '5xl' }}
              fontWeight="extrabold"
              color="white"
              lineHeight="1.1"
              letterSpacing="-0.03em"
              mb={6}
            >
              Find out exactly what
              <br />
              inefficiency is costing you.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.700" maxW="2xl" mx="auto" mb={8}>
              Three calculators. Three types of waste. Pick the one that matches
              your biggest problem right now.
            </Text>
            <HStack justify="center" spacing={8} flexWrap="wrap">
              {[
                { icon: FaBolt, label: 'Automation ROI', color: 'blue.300' },
                { icon: FaChartLine, label: 'Downtime Risk', color: 'red.300' },
                { icon: FaFileInvoiceDollar, label: 'Bill Audit', color: 'green.300' },
              ].map((item) => (
                <HStack key={item.label} spacing={2}>
                  <Icon as={item.icon} color={item.color} boxSize={4} />
                  <Text fontSize="sm" color="whiteAlpha.800" fontWeight="medium">{item.label}</Text>
                </HStack>
              ))}
            </HStack>
          </MotionBox>
        </Container>
      </Box>

      {/* Calculator + Case Stats */}
      <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} alignItems="flex-start">
          {/* Calculator */}
          <MotionBox
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 } as any}
          >
            <ROICalculator />
          </MotionBox>

          {/* Context column */}
          <MotionBox
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 } as any}
          >
            <VStack spacing={6} align="stretch">
              <Box>
                <Badge colorScheme="blue" mb={3}>Real Client Results</Badge>
                <Heading as="h2" fontSize="xl" fontWeight="extrabold" color={headingColor} mb={3}>
                  These numbers are from real engagements.
                </Heading>
                <Text fontSize="sm" color={textColor} lineHeight="1.7">
                  The calculator gives you a conservative estimate. Our actual results
                  for clients typically exceed it — because we look at total cost of
                  ownership, vendor pricing leverage, and automation compound effects
                  that a slider can't capture.
                </Text>
              </Box>

              <VStack spacing={4} align="stretch">
                {CASE_STATS.map((cs, i) => (
                  <MotionBox
                    key={cs.stat}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 } as any}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="xl"
                    p={5}
                  >
                    <HStack spacing={4}>
                      <Text
                        fontSize="2xl"
                        fontWeight="extrabold"
                        color={`${cs.color}.500`}
                        lineHeight="1"
                        minW="80px"
                      >
                        {cs.stat}
                      </Text>
                      <Text fontSize="sm" color={textColor} lineHeight="1.5">
                        {cs.label}
                      </Text>
                    </HStack>
                  </MotionBox>
                ))}
              </VStack>

              <Box
                bg={useColorModeValue('blue.50', 'blue.900')}
                border="1px solid"
                borderColor={useColorModeValue('blue.100', 'blue.800')}
                borderRadius="xl"
                p={5}
              >
                <Text fontSize="sm" fontWeight="bold" color={headingColor} mb={2}>
                  Want a real number, not an estimate?
                </Text>
                <Text fontSize="sm" color={textColor} mb={4} lineHeight="1.7">
                  Our Technology Bill Analysis takes your actual invoices and benchmarks
                  them against 200+ vendor contracts. You get a written report, not a slider.
                </Text>
                <Button
                  as="a"
                  href="/assessments?interest=bill-analysis"
                  colorScheme="blue"
                  size="sm"
                  w="full"
                >
                  Get the Real Analysis →
                </Button>
              </Box>
            </VStack>
          </MotionBox>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default CalculatorPage;
